import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useCanVisitSiteView } from 'features/overview'
import { PermissionsGate } from 'features/permissions'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import { NAV_ITEMS_CONFIG, NavItemConfig } from './navConfig'
import { ImageContainer, StyledLink } from './sharedStyles'
import { SelectedTab } from './types'

const MidSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto; /* Allow to grow and shrink, taking up available space */
  gap: 0.125rem;
`

const SearchInputContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 1rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.375rem;
  background-color: ${({ theme }) => theme.forecast.background2_5};
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: ${({ theme }) => theme.forecast.secondaryText};
  }

  &:focus {
    border-color: ${({ theme }) => theme.forecast.activeButton};
  }
`

const NavItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  outline: none;
  border-radius: 0.25rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.forecast.activeButton};
    outline-offset: 2px;
  }
`

const renderNavItem = (
  tabKey: SelectedTab | undefined,
  config: NavItemConfig,
  {
    siteID,
    orgID,
    selectedTab,
    handleNavigate,
    focusedIndex,
    currentIndex,
  }: {
    siteID: string | undefined
    orgID: string | undefined
    selectedTab: SelectedTab | undefined
    handleNavigate: (path: string, event: React.MouseEvent) => void
    focusedIndex: number
    currentIndex: number
  }
) => {
  const Icon = config.icon
  const params = config.params?.(siteID ?? '', orgID) ?? undefined
  const routePath = config.to.replace('{-$siteID}', siteID ?? '')

  const linkElement = (
    <StyledLink
      key={tabKey}
      to={config.to}
      params={params}
      $isSelected={focusedIndex === currentIndex}
      $isSelectedTab={selectedTab === tabKey}
      onClick={(e: React.MouseEvent) => handleNavigate(routePath, e)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          ;(e.currentTarget as HTMLAnchorElement).click()
        }
      }}
      tabIndex={focusedIndex === currentIndex ? 0 : -1}>
      <ImageContainer>
        <Icon />
      </ImageContainer>
      <p>{config.label}</p>
    </StyledLink>
  )

  if (config.permissions) {
    return (
      <PermissionsGate
        key={tabKey}
        requires={config.permissions}
        except={config.except}>
        {linkElement}
      </PermissionsGate>
    )
  }

  return linkElement
}

const SearchAppsInput: React.FC<{
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  autoFocus?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
}> = ({ value, onChange, autoFocus, inputRef }) => {
  return (
    <SearchInputContainer>
      <SearchInput
        ref={inputRef}
        type='text'
        placeholder='Search apps...'
        value={value}
        onChange={onChange}
        autoFocus={autoFocus}
      />
    </SearchInputContainer>
  )
}

export const MidSection: React.FC<{
  siteID: string | undefined
  orgID: string | undefined
  selectedTab: SelectedTab | undefined
  onCloseDrawer?: () => void
  autoFocus?: boolean
}> = ({ siteID, orgID, selectedTab, onCloseDrawer, autoFocus = false }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const navListRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const allowedNavbarItems = useNavbarConfig({ orgId: orgID ?? '' })

  const handleNavigate = (path: string, event: React.MouseEvent) => {
    event.stopPropagation()
    if (event.metaKey || event.ctrlKey) {
      window.open(path, '_blank')
      return
    }
    onCloseDrawer?.()
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, maxIndex: number) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setFocusedIndex((prev) => {
            const newIndex = prev === -1 ? 0 : (prev + 1) % maxIndex
            return newIndex
          })
          break
        case 'ArrowUp':
          event.preventDefault()
          setFocusedIndex((prev) => {
            const newIndex =
              prev === -1 ? maxIndex - 1 : (prev - 1 + maxIndex) % maxIndex
            return newIndex
          })
          break
        case 'Escape':
          event.preventDefault()
          setFocusedIndex(-1)
          break
      }
    },
    []
  )

  useEffect(() => {
    setFocusedIndex(-1)
  }, [searchTerm])

  useEffect(() => {
    if (autoFocus && searchInputRef.current) {
      // Small delay to ensure the drawer is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 150)
    }
  }, [autoFocus])

  const handleNavListFocus = () => {
    if (focusedIndex === -1) {
      setFocusedIndex(0)
    }
  }

  useEffect(() => {
    if (focusedIndex >= 0 && navListRef.current) {
      const links = navListRef.current.querySelectorAll('a')
      const targetLink = links[focusedIndex] as HTMLAnchorElement
      if (targetLink) {
        targetLink.focus()
      }
    }
  }, [focusedIndex])
  const canVisitSiteView = useCanVisitSiteView()

  const navItems = (
    Object.entries(NAV_ITEMS_CONFIG) as Array<[SelectedTab, NavItemConfig]>
  )
    // Filter by org-level allowed navbar items config
    .filter(([tabKey]) => {
      if (!allowedNavbarItems) return true
      return allowedNavbarItems.includes(tabKey)
    })
    // Filter by item-specific visibility rules
    .filter(([, config]) => {
      if (config.shouldShow) {
        return config.shouldShow({ siteID: siteID ?? '', canVisitSiteView })
      }
      return true
    })

  const filteredNavItems = navItems.filter(([, config]) => {
    if (!searchTerm) return true
    return config.label.toLowerCase().includes(searchTerm.toLowerCase())
  })

  return (
    <MidSectionContainer>
      <SearchAppsInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        autoFocus={autoFocus}
        inputRef={searchInputRef}
      />
      <NavItemsList
        ref={navListRef}
        tabIndex={0}
        onKeyDown={(e) => handleKeyDown(e, filteredNavItems.length)}
        onFocus={handleNavListFocus}>
        {filteredNavItems.map(([tabKey, config], index) =>
          renderNavItem(tabKey, config, {
            siteID,
            orgID,
            selectedTab,
            handleNavigate,
            focusedIndex,
            currentIndex: index,
          })
        )}
      </NavItemsList>
    </MidSectionContainer>
  )
}
